import { PipelineFilter } from 'src/components/pipelines/components/filters/types';
import { getPageNumber } from 'src/components/pipelines/pages/pipelines/utils';

type FilterQuery = {
  page: number;
  ['target.branch']?: string;
  ['target.selector.type']?: string;
  ['target.selector.pattern']?: string;
  ['creator.uuid']?: PipelineFilter['uuid'];
  status?: string[];
  trigger_type?: string;
  tags?: string[];
};

type Params = {
  repositoryFullSlug: string;
  branch: string;
  page: string;
};

export function buildPipelineFilterQuery(
  { branch, page }: Params,
  url: string
): FilterQuery {
  const filterQueries: Partial<FilterQuery> = {};
  const filters = parseFilterPathParam(url);

  if (branch) {
    filterQueries['target.branch'] = decodeURIComponent(branch);
  }

  if (filters.selectedPipelineType) {
    const pipelineTypeAndPattern = filters.selectedPipelineType.split(/:(.+)/);
    let type;
    switch (pipelineTypeAndPattern[0].trim().toUpperCase()) {
      case 'BRANCHES':
        type = 'BRANCH';
        break;
      case 'MERGE-QUEUES':
        type = 'MERGEQUEUES';
        break;
      case 'PULL-REQUESTS':
        type = 'PULLREQUESTS';
        break;
      case 'TAGS':
        type = 'TAG';
        break;
      default:
        type = pipelineTypeAndPattern[0].trim().toUpperCase();
    }
    filterQueries['target.selector.type'] = decodeURIComponent(type);
    if (pipelineTypeAndPattern.length > 1) {
      filterQueries['target.selector.pattern'] = decodeURIComponent(
        pipelineTypeAndPattern[1].trim()
      );
    }
  }

  if (filters.selectedStatus) {
    filterQueries.status = filters.selectedStatus.map(value =>
      value.toUpperCase()
    );
  }

  if (filters.selectedTriggerType) {
    filterQueries.trigger_type = filters.selectedTriggerType.toUpperCase();
  }

  if (filters.selectedTags) {
    filterQueries.tags = filters.selectedTags;
  }

  if (filters.uuid) {
    filterQueries['creator.uuid'] = filters.uuid;
  }

  return { ...filterQueries, page: getPageNumber(page) };
}

export function buildFilterPathParam(filters: PipelineFilter): string {
  let selectedStatusQuery;
  let selectedTriggerTypeQuery;
  let selectedPipelineTypeQuery;
  let selectedCurrentUserBuildsQuery;
  let selectedTagsQuery;

  if (filters && filters.selectedStatus) {
    selectedStatusQuery = `status=${filters.selectedStatus}`;
  }
  if (filters && filters.selectedTriggerType) {
    selectedTriggerTypeQuery = `triggerType=${filters.selectedTriggerType}`;
  }
  if (filters && filters.uuid) {
    selectedCurrentUserBuildsQuery = `selectedUser=${filters.uuid}`;
  }
  if (filters && filters.selectedPipelineType) {
    selectedPipelineTypeQuery = `pipelineType=${encodeURIComponent(
      filters.selectedPipelineType
    )}`;
  }

  if (filters && filters.selectedTags) {
    selectedTagsQuery = `tags=${filters.selectedTags}`;
  }

  const path = '/filters/';
  const queries = [
    selectedStatusQuery,
    selectedTriggerTypeQuery,
    selectedPipelineTypeQuery,
    selectedCurrentUserBuildsQuery,
    selectedTagsQuery,
  ]
    .filter(Boolean)
    .join('&');

  return queries ? `${path}[${queries}]` : '';
}

export function parseFilterPathParam(path: string): PipelineFilter {
  const filtersIndex = path.indexOf('/filters');
  if (filtersIndex === -1) {
    return {
      selectedStatus: null,
      selectedTriggerType: null,
      uuid: null,
      selectedPipelineType: null,
      selectedTags: null,
    };
  }

  const filterPath = path.substring(filtersIndex);
  const filterQuery = filterPath.substring(
    filterPath.indexOf('[') + 1,
    filterPath.lastIndexOf(']')
  );
  const filters = filterQuery.split('&');

  const findParamValue = (paramName: string) => {
    const param = filters.find(filter => filter.startsWith(`${paramName}=`));
    return param ? decodeURIComponent(param.split('=')[1]) : null;
  };

  return {
    selectedStatus: findParamValue('status')?.split(',') ?? null,
    selectedTriggerType: findParamValue('triggerType'),
    selectedPipelineType: findParamValue('pipelineType'),
    uuid: findParamValue('selectedUser'),
    selectedTags: findParamValue('tags')?.split(',') ?? null,
  };
}

export const getFilterQuery = (pathname: string) =>
  buildFilterPathParam(parseFilterPathParam(pathname));

export function getFilterPathname(
  page: number,
  pathname: string,
  branch: string
): string {
  const filterPath = getFilterQuery(pathname);
  if (branch) {
    return `/results/branch/${encodeURIComponent(
      branch
    )}/page/${page}${filterPath}`;
  }
  return `/results/page/${page}${filterPath}`;
}
